import _ from 'lodash';
import {
    Amount,
    ClaimEligibilityItem,
    EligibilityItemType,
    InPlanNetworkType,
    ServiceType,
} from '../../../../api';
import { normalize } from '../../eligibility/utils';
import { ITimelineEventStatusTagTone } from '../claim-timeline.types';
import {
    IEligibilityBenefitRow,
    IEligibilityPanelViewModel,
    TimelineEligibility,
} from './eligibility-panel.types';

const EMPTY_SERVICE_GROUP = 'NO_SERVICE_CODE';

const OTHER_INFO_TYPES = new Set<EligibilityItemType>([
    EligibilityItemType.BENEFITDISCLAIMER,
    EligibilityItemType.HEALTHCAREFACILITY,
    EligibilityItemType.MANAGEDCARECOORDINATOR,
    EligibilityItemType.PRIMARYCAREPROVIDER,
    EligibilityItemType.PRIORYEARHISTORY,
    EligibilityItemType.CONTACTFOLLOWINGENTITYFORELIGIBILITYORBENEFITINFORMATION,
    EligibilityItemType.OTHERSOURCEOFDATA,
]);

const IN_NETWORK_LABELS: Record<InPlanNetworkType, string> = {
    [InPlanNetworkType.NA]: 'Network not applicable',
    [InPlanNetworkType.UNKNOWN]: 'Network unknown',
    [InPlanNetworkType.YES]: 'In network',
    [InPlanNetworkType.NO]: 'Out of network',
};

function formatBenefitAmount(amount?: Amount): string {
    if (!amount?.amount) {
        return '—';
    }

    const value = Number.parseFloat(amount.amount);
    if (Number.isNaN(value)) {
        return amount.amount;
    }

    return `$${value.toFixed(2)}`;
}

function formatQuantityNote(item?: ClaimEligibilityItem): string | undefined {
    if (!item?.quantity) {
        return undefined;
    }

    const quantity = item.quantity;
    const quantityLabel = item.quantityType ? normalize(item.quantityType).toLowerCase() : 'units';
    const periodLabel = item.timePeriodType ? normalize(item.timePeriodType).toLowerCase() : undefined;

    return periodLabel ? `${quantity} ${quantityLabel} / ${periodLabel}` : `${quantity} ${quantityLabel}`;
}

function resolveItemsSource(eligibility: TimelineEligibility): {
    items: ClaimEligibilityItem[];
    memberSource: TimelineEligibility;
    hasValidationErrors: boolean;
} {
    const hasValidationErrors = (eligibility.validations?.length ?? 0) > 0;
    const dependent =
        !hasValidationErrors &&
        (!eligibility.items || eligibility.items.length === 0) &&
        eligibility.dependents?.[0]?.items?.length
            ? eligibility.dependents[0]
            : undefined;

    return {
        items: dependent?.items ?? eligibility.items ?? [],
        memberSource: dependent ?? eligibility,
        hasValidationErrors,
    };
}

function groupItemsByService(items: ClaimEligibilityItem[]): Map<string, ClaimEligibilityItem[]> {
    const groups = new Map<string, ClaimEligibilityItem[]>();

    items.forEach((item) => {
        if (!item.serviceTypeCodes?.length) {
            if (item.type && OTHER_INFO_TYPES.has(item.type)) {
                return;
            }

            groups.set(EMPTY_SERVICE_GROUP, [...(groups.get(EMPTY_SERVICE_GROUP) || []), item]);
            return;
        }

        item.serviceTypeCodes.forEach((code) => {
            if (code === ServiceType.HEALTHBENEFITPLANCOVERAGE) {
                return;
            }

            groups.set(code, [...(groups.get(code) || []), item]);
        });
    });

    return groups;
}

function findActiveCoverageItem(items: ClaimEligibilityItem[]): ClaimEligibilityItem | undefined {
    const healthBenefitItems = items.filter((item) =>
        item.serviceTypeCodes?.includes(ServiceType.HEALTHBENEFITPLANCOVERAGE),
    );
    const healthActive = healthBenefitItems.find((item) => item.type === EligibilityItemType.ACTIVECOVERAGE);
    if (healthActive) {
        return healthActive;
    }

    return items.find((item) => item.type === EligibilityItemType.ACTIVECOVERAGE);
}

function buildBenefitRow(serviceKey: string, groupItems: ClaimEligibilityItem[]): IEligibilityBenefitRow {
    const active = groupItems.find(
        (item) =>
            item.type === EligibilityItemType.ACTIVECOVERAGE ||
            (item.type != null && String(item.type).startsWith('ACTIVE')),
    );
    const inactive = groupItems.find((item) => item.type === EligibilityItemType.INACTIVE);
    const copayItem = _.maxBy(
        groupItems.filter((item) => item.type === EligibilityItemType.COPAYMENT && item.benefitAmount),
        (item) => Number.parseFloat(item.benefitAmount?.amount || '0'),
    );
    const limitation = groupItems.find((item) => item.type === EligibilityItemType.LIMITATION);

    let coverageLabel = 'Unknown';
    let coverageTone: ITimelineEventStatusTagTone = 'neutral';
    if (active) {
        coverageLabel = 'Active';
        coverageTone = 'success';
    } else if (inactive) {
        coverageLabel = 'Inactive';
        coverageTone = 'attention';
    }

    const noteParts = _.compact([
        ...(limitation?.messages || []),
        formatQuantityNote(limitation),
        ...(copayItem?.messages || []),
    ]);

    return {
        key: serviceKey,
        service: serviceKey === EMPTY_SERVICE_GROUP ? 'General benefits' : normalize(serviceKey),
        coverageLabel,
        coverageTone,
        copay: copayItem ? formatBenefitAmount(copayItem.benefitAmount) : '—',
        note: noteParts.length ? noteParts.join(' · ') : undefined,
    };
}

function extractOtherReported(items: ClaimEligibilityItem[]): string[] {
    const labels = items
        .filter((item) => item.type && OTHER_INFO_TYPES.has(item.type))
        .map((item) => normalize(item.type!));

    return _.uniq(labels);
}

function formatPlanType(planType?: string): string | undefined {
    if (!planType) {
        return undefined;
    }

    return planType.length > 3 ? normalize(planType) : planType;
}

function getClearinghouseName(eligibility: TimelineEligibility): string | undefined {
    if ('clearinghousePayer' in eligibility) {
        return eligibility.clearinghousePayer?.clearinghouse?.name;
    }

    return eligibility.informationSourceName;
}

function getClearinghousePayerLabel(eligibility: TimelineEligibility): string | undefined {
    if ('clearinghousePayer' in eligibility) {
        const payer = eligibility.clearinghousePayer?.payer?.name;
        const externalId = eligibility.clearinghousePayer?.externalId;
        if (!payer) {
            return undefined;
        }

        return externalId ? `${payer} [${externalId}]` : payer;
    }

    return eligibility.informationSourceName;
}

function getPriorYearHistory(items: ClaimEligibilityItem[]): string | undefined {
    const priorYearItem = items.find((item) => item.type === EligibilityItemType.PRIORYEARHISTORY);
    if (!priorYearItem) {
        return undefined;
    }

    return priorYearItem.coverageDescription || priorYearItem.messages?.join(' · ');
}

export function buildEligibilityPanelViewModel(eligibility: TimelineEligibility): IEligibilityPanelViewModel {
    const { items, memberSource, hasValidationErrors } = resolveItemsSource(eligibility);
    const activeCoverage = findActiveCoverageItem(items);
    const groupedItems = groupItemsByService(items);

    const benefitRows = [...groupedItems.entries()]
        .sort(([keyA], [keyB]) => {
            if (keyA === ServiceType.MEDICALCARE) {
                return -1;
            }
            if (keyB === ServiceType.MEDICALCARE) {
                return 1;
            }

            return keyA.localeCompare(keyB);
        })
        .map(([serviceKey, groupItems]) => buildBenefitRow(serviceKey, groupItems));

    const activeRows = benefitRows.filter((row) => row.coverageLabel === 'Active');
    const allZeroCopay = benefitRows.every((row) => row.copay === '$0.00' || row.copay === '—');

    const coverageTo = eligibility.planEndDate || activeCoverage?.eligibilityThroughDate;
    const coverageOngoing = !coverageTo || coverageTo.startsWith('9999');

    const validationMessages =
        eligibility.validations?.map(
            (validation) =>
                `Reason: ${validation.rejectReasonType || 'Unknown'}, Suggested action: ${
                    validation.followupActionType || 'None'
                }`,
        ) ?? [];

    let statusLabel = 'Coverage unknown';
    let statusTone: ITimelineEventStatusTagTone = 'neutral';
    if (hasValidationErrors) {
        statusLabel = 'Validation failed';
        statusTone = 'attention';
    } else if (activeCoverage) {
        statusLabel = 'Active coverage';
        statusTone = 'success';
    } else if (items.some((item) => item.type === EligibilityItemType.INACTIVE)) {
        statusLabel = 'Inactive coverage';
        statusTone = 'attention';
    }

    const memberName = _.compact([memberSource.first, memberSource.middle, memberSource.last]).join(' ');

    return {
        hasValidationErrors,
        statusLabel,
        statusTone,
        insuranceType: formatPlanType(activeCoverage?.planType),
        plan: eligibility.planDescription || activeCoverage?.coverageDescription,
        payer: getClearinghousePayerLabel(eligibility),
        network: activeCoverage?.inPlanNetworkType
            ? IN_NETWORK_LABELS[activeCoverage.inPlanNetworkType]
            : undefined,
        level: activeCoverage?.coverageLevelType ? normalize(activeCoverage.coverageLevelType) : undefined,
        groupId: eligibility.groupId,
        coverageFrom: eligibility.planStartDate || activeCoverage?.eligibilityFromDate,
        coverageTo: coverageOngoing ? undefined : coverageTo,
        coverageOngoing,
        memberName,
        dob: memberSource.dob,
        gender: memberSource.sex ? normalize(memberSource.sex) : undefined,
        address: undefined,
        memberId: eligibility.otherId,
        memberQualifier: eligibility.otherQualifierId,
        verified: eligibility.created,
        eligibilityId: eligibility.id,
        externalId: 'externalId' in eligibility ? eligibility.externalId : undefined,
        eligibilityType: 'type' in eligibility ? eligibility.type : undefined,
        clearinghouse: getClearinghouseName(eligibility),
        clearinghousePayer: getClearinghousePayerLabel(eligibility),
        priorYear: getPriorYearHistory(items),
        benefitRows,
        otherReported: extractOtherReported(items),
        summaryText:
            !hasValidationErrors && activeRows.length
                ? `All ${activeRows.length} service categor${activeRows.length === 1 ? 'y' : 'ies'} active${
                      allZeroCopay ? ' with $0 member copay' : ''
                  }.`
                : undefined,
        validationMessages,
    };
}
